import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Loader } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import useSongs from '../../hooks/useSongs';
import AddSongForm from './AddSongForm';
import SongCard from './SongCard';

export default function SongsTab({ user, bandId, isPremium }) {
  const [expandedSong, setExpandedSong] = useState(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);
  const containerRef = useRef(null);
  const dragStateRef = useRef({ draggedIndex: null, insertIndex: null });
  const ghostRef = useRef(null);
  const draggedElementRef = useRef(null);

  const isMobile = useIsMobile();

  // Use the custom hook for all Firebase operations
  const {
    songs,
    ideas,
    firebaseReady,
    loading,
    error,
    uploadProgress,
    addingFromIdea,
    commenterName,
    sortedSongs,
    canAddSong,
    canAddVersion,
    getVersionCount,
    getVersionsArray,
    getUnreadCommentCount,
    setError,
    addSong,
    updateSongName,
    deleteSong,
    handleAudioUpload,
    handleLyricsUpload,
    deleteLyrics,
    updateLyricsText,    // NEW
    uploadSongFile,      // NEW
    deleteSongFile,      // NEW
    deleteVersion,
    updateVersionNotes,
    addComment,
    deleteComment,
    addIdeaToSong,
    saveSongOrder,
    markSongCommentsAsRead,
    FREE_SONG_LIMIT,
    FREE_VERSION_LIMIT,
  } = useSongs({ user, bandId, isPremium });

  // Mark comments as read when song is expanded
  useEffect(() => {
    if (expandedSong && user && bandId) {
      markSongCommentsAsRead(expandedSong);
    }
  }, [expandedSong, user, bandId, markSongCommentsAsRead]);

  // Drag handlers - with visual ghost that follows cursor/finger
  const createGhost = (element, y) => {
    const rect = element.getBoundingClientRect();
    const ghost = element.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.width = `${rect.width}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${y - 30}px`;
    ghost.style.opacity = '0.9';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '9999';
    ghost.style.transform = 'scale(1.02)';
    ghost.style.boxShadow = '0 10px 40px rgba(129, 70, 255, 0.4)';
    ghost.style.borderRadius = '8px';
    ghost.style.transition = 'none';
    ghost.id = 'drag-ghost';
    document.body.appendChild(ghost);
    return ghost;
  };

  const moveGhost = (y) => {
    if (!ghostRef.current) return;
    ghostRef.current.style.top = `${y - 30}px`;
  };

  const removeGhost = () => {
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  const calculateInsertIndex = (y, fromIndex) => {
    if (!containerRef.current) return null;

    const elements = containerRef.current.querySelectorAll('[data-song-index]');
    let newInsert = null;

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const idx = parseInt(el.dataset.songIndex, 10);
      const mid = rect.top + rect.height / 2;

      if (y < mid && y >= rect.top - 20) {
        if (idx !== fromIndex && idx !== fromIndex + 1) newInsert = idx;
      } else if (y >= mid && y <= rect.bottom + 20) {
        if (idx + 1 !== fromIndex && idx !== fromIndex) newInsert = idx + 1;
      }
    });

    return newInsert;
  };

  const performReorder = () => {
    const { draggedIndex: fromIndex, insertIndex: toInsert } = dragStateRef.current;

    if (fromIndex !== null && toInsert !== null) {
      let target = toInsert > fromIndex ? toInsert - 1 : toInsert;
      if (target !== fromIndex) {
        const order = sortedSongs.map(s => s.id);
        const [moved] = order.splice(fromIndex, 1);
        order.splice(target, 0, moved);
        saveSongOrder(order);
      }
    }
  };

  const resetDragState = () => {
    removeGhost();
    if (draggedElementRef.current) {
      draggedElementRef.current.style.opacity = '1';
      draggedElementRef.current.style.transform = '';
      draggedElementRef.current = null;
    }
    dragStateRef.current = { draggedIndex: null, insertIndex: null };
    setDraggedIndex(null);
    setInsertIndex(null);
  };

  // Mouse drag handlers (desktop)
  const handleMouseDown = (e, index) => {
    if (isMobile) return;
    e.preventDefault();

    const element = e.currentTarget.closest('[data-song-index]');
    draggedElementRef.current = element;
    element.style.opacity = '0.4';
    element.style.transform = 'scale(0.98)';

    setDraggedIndex(index);
    dragStateRef.current.draggedIndex = index;

    ghostRef.current = createGhost(element, e.clientY);

    const handleMouseMove = (moveEvent) => {
      moveGhost(moveEvent.clientY);

      const newInsert = calculateInsertIndex(moveEvent.clientY, dragStateRef.current.draggedIndex);
      if (newInsert !== dragStateRef.current.insertIndex) {
        dragStateRef.current.insertIndex = newInsert;
        setInsertIndex(newInsert);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      performReorder();
      resetDragState();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Touch drag handlers (mobile)
  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    const element = e.currentTarget.closest('[data-song-index]');

    draggedElementRef.current = element;
    element.style.opacity = '0.4';
    element.style.transform = 'scale(0.98)';

    setDraggedIndex(index);
    dragStateRef.current.draggedIndex = index;

    ghostRef.current = createGhost(element, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (dragStateRef.current.draggedIndex === null) return;
    e.preventDefault();

    const touch = e.touches[0];
    moveGhost(touch.clientY);

    const newInsert = calculateInsertIndex(touch.clientY, dragStateRef.current.draggedIndex);
    if (newInsert !== dragStateRef.current.insertIndex) {
      dragStateRef.current.insertIndex = newInsert;
      setInsertIndex(newInsert);
    }
  };

  const handleTouchEnd = () => {
    performReorder();
    resetDragState();
  };

  // Cleanup ghost on unmount
  useEffect(() => {
    return () => removeGhost();
  }, []);

  const handleToggleExpand = (songId) => {
    setExpandedSong(prev => prev === songId ? null : songId);
  };

  const handleDeleteSong = async (songId) => {
    const success = await deleteSong(songId);
    if (success) {
      setExpandedSong(null);
    }
  };

  if (!firebaseReady) {
    return (
      <div className="card-3d rounded-card p-6 flex items-center justify-center gap-3">
        <Loader className="w-5 h-5 text-white animate-spin" />
        <span className="text-text-muted">Connecting to Firebase...</span>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="bg-red-900/50 rounded-card p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-100 text-sm">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

      <AddSongForm
        canAddSong={canAddSong}
        loading={loading}
        isMobile={isMobile}
        onAddSong={addSong}
        FREE_SONG_LIMIT={FREE_SONG_LIMIT}
      />

      <div ref={containerRef} className="space-y-3">
        {sortedSongs.length === 0 ? (
          <div className="text-center py-12 text-text-subtle">
            <p>No songs yet. Add your first song above!</p>
          </div>
        ) : (
          sortedSongs.map((song, index) => (
            <SongCard
              key={song.id}
              song={song}
              index={index}
              isExpanded={expandedSong === song.id}
              onToggleExpand={handleToggleExpand}
              isMobile={isMobile}
              isPremium={isPremium}
              user={user}
              ideas={ideas}
              uploadProgress={uploadProgress}
              addingFromIdea={addingFromIdea}
              commenterName={commenterName}
              canAddVersion={canAddVersion}
              getVersionCount={getVersionCount}
              getVersionsArray={getVersionsArray}
              getUnreadCommentCount={getUnreadCommentCount}
              onUpdateSongName={updateSongName}
              onDeleteSong={handleDeleteSong}
              onAudioUpload={handleAudioUpload}
              onLyricsUpload={handleLyricsUpload}
              onDeleteLyrics={deleteLyrics}
              onUpdateLyricsText={updateLyricsText}
              onUploadSongFile={uploadSongFile}
              onDeleteSongFile={deleteSongFile}
              onDeleteVersion={deleteVersion}
              onUpdateNotes={updateVersionNotes}
              onAddComment={addComment}
              onDeleteComment={deleteComment}
              onAddIdeaToSong={addIdeaToSong}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              draggedIndex={draggedIndex}
              insertIndex={insertIndex}
              FREE_VERSION_LIMIT={FREE_VERSION_LIMIT}
              loading={loading}
            />
          ))
        )}
      </div>
    </>
  );
}
